import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import {
  MdWifi, MdPower, MdLocalCafe, MdEventSeat, MdFastfood,
  MdWork, MdAccessible, MdVolumeOff, MdVolumeDown, MdVolumeUp
} from 'react-icons/md';
import { FaChalkboard, FaTv } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Constants
const NOISE_LEVEL_ICONS = {
  Quiet: MdVolumeOff,
  Moderate: MdVolumeDown,
  Busy: MdVolumeUp,
};

const FEATURE_ICONS = {
  Wifi: MdWifi,
  'Power Outlets': MdPower,
  Whiteboard: FaChalkboard,
  Cafe: MdLocalCafe,
  Couches: MdEventSeat,
  FoodAndDrinks: MdFastfood,
  privateDesks: MdWork,
  accessible: MdAccessible,
  projector: FaTv
};

const SharedDetailsPage = ({ data, type, RightColumnComponent, loading }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = type === 'listing' ? data?.imageURL : data?.photos;

  const handleImageNavigation = {
    next: () => {
      setCurrentImageIndex((prev) => 
        prev === (images?.length || 1) - 1 ? 0 : prev + 1
      );
    },
    previous: () => {
      setCurrentImageIndex((prev) => 
        prev === 0 ? (images?.length || 1) - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xl text-center text-gray-400">
            {type === 'listing' ? 'Study spot' : 'Room'} not found
          </p>
        </div>
      </div>
    );
  }

  const renderImageGallery = () => {
    if (!images || images.length === 0) return null;

    return (
      <div className="relative rounded-2xl overflow-hidden bg-[#111] border border-white/10 aspect-video">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentImageIndex}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <img
              src={images[currentImageIndex]}
              alt={type === 'listing' ? data.name : `${data.building} ${data.room}`}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>

        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        {images.length > 1 && renderImageNavigation()}
        
        {/* Image Dots */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  idx === currentImageIndex ? 'bg-white w-3' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderImageNavigation = () => (
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 pointer-events-none">
      <motion.button
        onClick={handleImageNavigation.previous}
        className="pointer-events-auto p-2 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition-colors backdrop-blur-sm"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ChevronLeft className="w-6 h-6" />
      </motion.button>
      <motion.button
        onClick={handleImageNavigation.next}
        className="pointer-events-auto p-2 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition-colors backdrop-blur-sm"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ChevronRight className="w-6 h-6" />
      </motion.button>
    </div>
  );

  const renderInfo = () => (
    <div className="bg-[#111] rounded-2xl border border-white/10 p-6">
      {type === 'listing' ? (
        <>
          <h1 className="text-2xl font-bold text-white mb-2">{data.name}</h1>
          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <MapPin className="w-4 h-4" />
              <span>{data.location}</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-700" />
            <span className="px-2 py-0.5 rounded-full bg-white/10 text-gray-300 text-xs font-medium">
              {data.spaceType}
            </span>
            <div className="w-1 h-1 rounded-full bg-gray-700" />
            <div className="flex items-center gap-1.5 text-gray-300">
              {React.createElement(NOISE_LEVEL_ICONS[data.noiseLevel] || NOISE_LEVEL_ICONS.Moderate, { className: "w-4 h-4" })}
              <span>{data.noiseLevel || 'Moderate'} Noise</span>
            </div>
          </div>
          
          {data.description && (
            <div className="mb-8">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">About</h3>
              <p className="text-gray-300 leading-relaxed">{data.description}</p>
            </div>
          )}
        </>
      ) : (
        <>
          <h1 className="text-2xl font-bold text-white mb-2">
            {data.building} - {data.room}
          </h1>
          <p className="text-gray-400 mb-6">{data.campus}</p>
        </>
      )}

      {renderFeatures()}
    </div>
  );

  const renderFeatures = () => {
    if (!data.features?.length) return null;

    return (
      <div>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Amenities</h3>
        <div className="grid grid-cols-2 gap-3">
          {data.features.map((feature, index) => {
            const IconComponent = FEATURE_ICONS[feature];
            return (
              <div
                key={index}
                className="flex items-center gap-2 p-3 bg-white/5 rounded-xl border border-white/5"
              >
                {IconComponent && <IconComponent className="w-4 h-4 text-purple-400" />}
                <span className="text-gray-300 text-sm">{feature}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <button 
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
          <span>Back</span>
        </button>

        <div className="lg:grid lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6">
            {renderImageGallery()}
            {renderInfo()}
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-4 mt-6 lg:mt-0">
            {RightColumnComponent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedDetailsPage;